import { useState } from 'react';
import { useProcessingFee } from '@/hooks/useProcessingFee';
import { ratePercent } from '@/types/advisor-billing';
import { apiError } from '@/lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check, Loader2, Plus, Send, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import OfferEditor, { emptyDraft, type OfferDraft } from '@/components/offers/OfferEditor';
import PackageCard from '@/components/offers/PackageCard';
import { OFFER_STATUS_TONE } from '@/components/offers/statusTone';
import { useClientScope } from '@/context/ClientScopeProvider';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { money, type Offer } from '@/types/offer';
import { ServerContractsSection } from '@/components/contracts/ServerContracts';


export default function ClientOffersPage() {
  const { data: fee } = useProcessingFee();
  const feeRate = fee && parseFloat(fee.rate_percent) > 0 ? ratePercent(fee.rate_percent) : null;
  const feeLabel = feeRate ? `${feeRate} platform fee` : 'No platform fee';
  const { clientId, client } = useClientScope();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<OfferDraft | null>(null);

  const { data: offers, isLoading } = useQuery<Offer[]>({
    queryKey: ['client-offers', clientId],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/offers/', { params: { client: clientId } });
      return res.data?.results ?? res.data ?? [];
    },
    enabled: Boolean(clientId),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['client-offers', clientId] });

  const create = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/advisor/offers/', { ...draft, client: clientId });
    },
    onSuccess: () => {
      toast.success('Offer saved as a draft.');
      setDraft(null);
      refresh();
    },
    onError: (error) => toast.error(apiError(error, 'Could not save the offer.')),
  });

  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const act = useMutation({
    mutationFn: async ({ id, what, reason }: { id: string; what: string; reason?: string }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/advisor/offers/${id}/${what}/`, reason ? { reason } : {});
    },
    onSuccess: (_data, { what }) => {
      toast.success(
        what === 'send' ? 'Sent to the client.'
          : what === 'end' ? 'Contract ended.'
          : what === 'confirm' ? 'Confirmed. This is now the contract.'
          : what === 'reject' ? 'Turned down; the client can read why in the conversation.'
          : 'Offer withdrawn.',
      );
      setRejecting(null);
      setRejectReason('');
      refresh();
    },
    onError: (error) => toast.error(apiError(error, 'That did not work.')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/advisor/offers/${id}/`);
    },
    onSuccess: () => { toast.success('Draft deleted.'); refresh(); },
    onError: (error) => toast.error(apiError(error, 'Could not delete it.')),
  });

  return (
    <div className="space-y-4" data-testid="client-offers-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Offers &amp; contracts</h2>
          <p className="text-muted-foreground">
            The servers {client.company_name} pays for, what you are proposing to them, and what they agreed to.
          </p>
        </div>
        {!draft && (
          <Button onClick={() => setDraft(emptyDraft(client?.invoiced_in ?? undefined))} data-testid="new-offer-btn">
            <Plus className="mr-2 h-4 w-4" />
            New offer
          </Button>
        )}
      </div>

      {client.customer_organization_id && (
        <ServerContractsSection orgId={client.customer_organization_id} owner="Their" />
      )}

      {draft && (
        <OfferEditor
          draft={draft}
          setDraft={setDraft}
          onSave={() => create.mutate()}
          onCancel={() => setDraft(null)}
          saving={create.isPending}
          feeLabel={feeLabel}
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (offers ?? []).length === 0 && !draft ? (
        <p
          className="py-12 text-center text-sm text-muted-foreground"
          data-testid="client-offers-empty"
        >
          You have not offered {client.company_name} anything yet.
        </p>
      ) : (
        <div className="space-y-4" data-testid="offer-list">
          {(offers ?? []).map((offer) => (
            <Card key={offer.id} data-testid={`offer-${offer.id}`}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{offer.title}</CardTitle>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {offer.billing_mode === 'via_aoc' ? 'Billed through BitSwan' : 'You invoice directly'} · {offer.currency}
                      {offer.is_running && offer.monthly_fee
                        && ` · ${money(offer.monthly_fee, offer.currency)} a month`}
                      {offer.ended_at && ' · ended'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      className={OFFER_STATUS_TONE[offer.status]}
                      data-testid={`offer-status-${offer.id}`}
                    >
                      {offer.ended_at ? 'Ended' : offer.status_display}
                    </Badge>
                    {offer.status === 'draft' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => act.mutate({ id: offer.id, what: 'send' })}
                          disabled={act.isPending}
                          data-testid={`send-offer-${offer.id}`}
                        >
                          <Send className="mr-2 h-3.5 w-3.5" />
                          Send it
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => remove.mutate(offer.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {offer.status === 'sent' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act.mutate({ id: offer.id, what: 'withdraw' })}
                        data-testid={`withdraw-offer-${offer.id}`}
                      >
                        Withdraw
                      </Button>
                    )}
                    {offer.status === 'requested' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => act.mutate({ id: offer.id, what: 'confirm' })}
                          disabled={act.isPending}
                          data-testid={`confirm-request-${offer.id}`}
                        >
                          <Check className="mr-2 h-3.5 w-3.5" />
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setRejecting(offer.id); setRejectReason(''); }}
                          disabled={act.isPending}
                          data-testid={`reject-request-${offer.id}`}
                        >
                          Turn down
                        </Button>
                      </>
                    )}
                    {offer.is_running && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act.mutate({ id: offer.id, what: 'end' })}
                        data-testid={`end-contract-${offer.id}`}
                      >
                        End contract
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {offer.status === 'requested' && (
                  <p className="text-sm text-muted-foreground" data-testid={`request-note-${offer.id}`}>
                    {offer.company_name} asked for this from your public profile
                    {offer.accepted_by_email ? ` (${offer.accepted_by_email})` : ''}. Nothing is agreed until you confirm it.
                  </p>
                )}
                {rejecting === offer.id && (
                  <div className="space-y-2 rounded-lg border bg-muted/30 p-3" data-testid={`reject-form-${offer.id}`}>
                    <p className="text-sm font-medium">Why can you not take this on? The client reads it in your conversation.</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        'I have no capacity for a new client at the moment.',
                        'This is outside what I can do well; someone else will serve you better.',
                        'I can take you on from next month; write to me and we will agree a date.',
                      ].map((text) => (
                        <button
                          key={text}
                          type="button"
                          onClick={() => setRejectReason(text)}
                          className={`rounded-full border px-2.5 py-1 text-xs ${rejectReason === text ? 'border-primary bg-primary/10' : 'hover:border-primary/50'}`}
                          data-testid={`reject-reason-pick-${offer.id}`}
                        >
                          {text}
                        </button>
                      ))}
                    </div>
                    <textarea
                      className="min-h-[60px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Or say it in your own words."
                      data-testid={`reject-reason-${offer.id}`}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => act.mutate({ id: offer.id, what: 'reject', reason: rejectReason })}
                        disabled={act.isPending || !rejectReason.trim()}
                        data-testid={`reject-request-send-${offer.id}`}
                      >
                        Turn it down
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRejecting(null)}>Cancel</Button>
                    </div>
                  </div>
                )}
                {offer.status === 'rejected' && offer.rejection_reason && (
                  <p className="text-sm text-muted-foreground">You said: {offer.rejection_reason}</p>
                )}
                {offer.introduction && (
                  <p className="text-sm text-muted-foreground">{offer.introduction}</p>
                )}
                {offer.status === 'declined' && offer.decline_reason && (
                  <p className="text-sm text-muted-foreground">
                    They said: {offer.decline_reason}
                  </p>
                )}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {offer.packages.map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      currency={offer.currency}
                      chosen={offer.accepted_package === pkg.id}
                      testId={`offer-package-${pkg.id}`}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
